import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/AuthContext'
import {
  createContactoComercial, updateContactoComercial, getContactosComercial, getEtiquetasContactoComercial,
  createVinculoComercial,
} from '../../api/client'
import { puede, mensajeError, coincidenciasDuplicado, ordenarCatalogo } from './comercialLogica'
import {
  formularioDe, pideOrganizaciones, datosContacto, vinculosParaEnviar, hayCambios, agregarFila, quitarFila,
  marcarPrincipal, cambiarFila, correoValido, duplicadosPorNombre, coincidenciaDeContacto,
} from './contactosLogica'
import { fijarGuardia } from './useFichaComercial'
import ComercialAvisoDuplicados from './ComercialAvisoDuplicados'
import ComercialSelectorOrganizacion from './ComercialSelectorOrganizacion'
import ComercialOrganizacionForm from './ComercialOrganizacionForm'

const entrada = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200'

// A contact's form, drawn inside its side panel (like the organization's: no
// modal). It takes the organizations to link (one row each, with its cargo;
// one empty row from the start; `desde` comes already chosen when created from
// an organization's card) when creating, and when editing one that has none --
// otherwise links are edited in the card's Organizaciones tab. Saving a new one
// goes through three warnings, none of them blocking: no organization, same name
// and surnames (checked here), same email (the server's 409 posibles_duplicados).
// Editing sends only what changed, then links the organizations chosen. As the
// organization's form: editing guards unsaved changes when leaving; creating just drops them.
// An organization row's selector can also create one: the E5 organization form
// takes this panel's place (this form stays alive underneath, nothing typed is
// lost), and saving it comes back here with the new one chosen in that row.
//   contacto: the card being edited (null = a new one); desde: a new one's organization
//   onGuardado(ficha); onCancelar(): left without saving (already confirmed)
//   onSubformulario(volver | null): the organization form shows (with how to come
//     back: the panel's "‹" does that then) or no longer does (the panel's title)
//   onOrganizacionCreada(): one was created on the way (the page's list may reload)
export default function ComercialContactoForm({ contacto, desde, onGuardado, onCancelar, onSubformulario, onOrganizacionCreada }) {
  const { t } = useTranslation('comercial')
  const { user } = useAuth()
  const puedeCrearOrg = puede(user, 'comercial_editar')
  const inicial = useMemo(() => formularioDe(contacto, desde), [contacto, desde])
  const pide = pideOrganizaciones(contacto)
  const [form, setForm] = useState(inicial)
  const [etiquetas, setEtiquetas] = useState([])
  const [errores, setErrores] = useState({})            // { nombre, correo-<i> }
  const [error, setError] = useState('')
  const [aviso, setAviso] = useState(null)              // { tipo: 'sinorg' } | { tipo: 'nombre' | 'correo', coincidencias }
  const [guardando, setGuardando] = useState(false)
  const [creandoOrg, setCreandoOrg] = useState(null)    // { fila, nombre } while the organization form shows
  const avisoRef = useRef(null)
  const scrollRef = useRef(0)                           // where the form was scrolled, to come back to it

  const volverRef = useRef(null)
  const enSub = !!creandoOrg
  useEffect(() => { onSubformulario?.(enSub ? () => volverRef.current?.() : null) }, [enSub, onSubformulario])
  // While the organization form shows, Esc goes back to the contact (not the whole panel away).
  useEffect(() => {
    if (!creandoOrg) return undefined
    const esc = (e) => { if (e.key === 'Escape') setCreandoOrg(null) }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [creandoOrg])

  const sucio = !!contacto && hayCambios(form, inicial)    // only an existing one guards its changes
  const sucioRef = useRef(false)
  useEffect(() => { sucioRef.current = sucio }, [sucio])
  useEffect(() => fijarGuardia(() => !sucioRef.current || window.confirm(t('form.descartar'))), [t])
  useEffect(() => {
    if (!sucio) return undefined
    const avisar = (e) => { e.preventDefault(); e.returnValue = '' }      // reload or close the tab
    window.addEventListener('beforeunload', avisar)
    return () => window.removeEventListener('beforeunload', avisar)
  }, [sucio])

  useEffect(() => {
    getEtiquetasContactoComercial().then(d => setEtiquetas(ordenarCatalogo(d))).catch(() => {})
  }, [])
  useEffect(() => { if (aviso) avisoRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) }, [aviso])

  const cambiar = (campo, valor) => {
    setForm(f => ({ ...f, [campo]: valor }))
    setAviso(null)
    if (campo === 'nombre') setErrores(e => ({ ...e, nombre: '' }))
  }
  const cambiarLista = (campo, fn) => {
    setForm(f => ({ ...f, [campo]: fn(f[campo]) }))
    setAviso(null)
    if (campo === 'correos') setErrores(e => ({ nombre: e.nombre }))
  }
  const alternarEtiqueta = (id) => cambiar('etiqueta_ids',
    form.etiqueta_ids.includes(id) ? form.etiqueta_ids.filter(x => x !== id) : [...form.etiqueta_ids, id])
  // Active labels, plus an inactive one it already has (kept, marked; it can't be given anew).
  const etiquetasOfrecidas = etiquetas.filter(x => x.activo || inicial.etiqueta_ids.includes(x.id))

  // pasos: the warnings already accepted on this attempt -- sinorg, nombre, correo.
  const guardar = async (pasos = {}) => {
    const errs = {}
    if (!form.nombre.trim()) errs.nombre = t('formContacto.nombreObligatorio')
    form.correos.forEach((x, i) => { if (x.valor.trim() && !correoValido(x.valor)) errs[`correo-${i}`] = t('camposContacto.correoInvalido') })
    setErrores(errs)
    if (Object.keys(errs).length) return
    const datos = datosContacto(form, contacto ? inicial : null)
    const nuevos = contacto && pide ? vinculosParaEnviar(form.vinculos) : []      // links for one that had none
    if (contacto && !Object.keys(datos).length && !nuevos.length) { onCancelar(); return }
    if (!contacto && !datos.vinculos.length && !pasos.sinorg) { setAviso({ tipo: 'sinorg' }); return }

    setGuardando(true)
    setError('')
    try {
      if (!contacto && !pasos.nombre) {
        // Every contact (archived too), compared here: the server's search minds accents ("Maria" ≠ "María").
        const mismos = duplicadosPorNombre(await getContactosComercial({ archivados: 'todos' }), form.nombre, form.apellidos)
        if (mismos.length) {
          setAviso({ tipo: 'nombre', coincidencias: mismos.map(coincidenciaDeContacto), pasos })
          setGuardando(false)
          return
        }
      }
      if (pasos.correo) datos.confirmar_duplicados = true
      let ficha
      if (!contacto) ficha = await createContactoComercial(datos)
      else {
        ficha = Object.keys(datos).length ? await updateContactoComercial(contacto.id, datos) : contacto
        for (const v of nuevos) {
          await createVinculoComercial(contacto.id, v)
          // Linked: out of the form, so a retry after a later failure doesn't link it twice.
          setForm(f => ({ ...f, vinculos: f.vinculos.filter(x => x.organizacion_id !== v.organizacion_id) }))
        }
      }
      sucioRef.current = false
      onGuardado(ficha)
    } catch (err) {
      const c = coincidenciasDuplicado(err)
      if (c) setAviso({ tipo: 'correo', coincidencias: c, pasos })
      else setError(mensajeError(err, t, t('form.fallo')))
      setGuardando(false)
    }
  }
  const seguir = () => {
    const pasos = { ...(aviso?.pasos || {}), [aviso.tipo]: true }
    setAviso(null)
    guardar(pasos)
  }
  const cancelar = () => {
    if (sucioRef.current && !window.confirm(t('form.descartar'))) return
    sucioRef.current = false
    onCancelar()
  }

  // One link per organization: the other rows' choices, and any (inactive) link the contact already has.
  const elegidas = [...form.vinculos.map(v => v.organizacion_id), ...(contacto?.vinculos || []).map(v => v.organizacion_id)]
    .filter(Boolean)
  const abrirCrearOrg = (fila, nombre) => {
    scrollRef.current = avisoRef.current?.closest('.overflow-y-auto')?.scrollTop || 0
    setCreandoOrg({ fila, nombre })
  }
  const volverDeOrg = () => {
    setCreandoOrg(null)
    requestAnimationFrame(() => {                 // back where the contact's form was
      const caja = avisoRef.current?.closest('.overflow-y-auto')
      if (caja) caja.scrollTop = scrollRef.current
    })
  }
  useEffect(() => { volverRef.current = volverDeOrg })
  const crearEnFila = (i) => puedeCrearOrg && {
    etiqueta: (nombre) => (nombre ? t('formContacto.crearOrganizacionNombre', { nombre }) : t('formContacto.crearOrganizacion')),
    onCrear: (nombre) => abrirCrearOrg(i, nombre),
  }

  // --- Creating an organization for one of the rows (this form waits, alive, underneath) ---
  if (creandoOrg) {
    const quien = [form.nombre, form.apellidos].filter(x => x?.trim()).join(' ').trim()
    return (
      <div data-comercial-modal className="flex flex-col min-h-full">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2">
          <p className="text-xs text-sky-900">
            {t('formContacto.creandoOrganizacion', { contacto: quien || t('formContacto.esteContacto') })}
          </p>
          <button type="button" onClick={volverDeOrg} className="text-xs font-medium text-sky-800 underline hover:text-sky-950">
            ‹ {t('formContacto.volverContacto')}
          </button>
        </div>
        <ComercialOrganizacionForm org={null} nombreInicial={creandoOrg.nombre} conGuardia={false}
          onCancelar={volverDeOrg}
          onGuardado={org => {
            onOrganizacionCreada?.()
            setForm(f => ({ ...f, vinculos: cambiarFila(f.vinculos, creandoOrg.fila, 'organizacion_id', org.id) }))
            setAviso(null)
            volverDeOrg()
          }} />
      </div>
    )
  }

  return (
    <form noValidate onSubmit={e => { e.preventDefault(); guardar() }} className="flex flex-col min-h-full">
      <div className="space-y-5 flex-1">
        <div ref={avisoRef}>
          {aviso?.tipo === 'sinorg' && (
            <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-900">{t('formContacto.sinOrganizacion')}</p>
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button type="button" onClick={() => setAviso(null)}
                  className="px-4 py-2 rounded-lg text-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                  {t('formContacto.volverEditar')}
                </button>
                <button type="button" onClick={seguir} disabled={guardando}
                  className="px-4 py-2 rounded-lg text-sm bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40">
                  {t('duplicados.guardarIgual')}
                </button>
              </div>
            </div>
          )}
          {(aviso?.tipo === 'nombre' || aviso?.tipo === 'correo') && (
            <ComercialAvisoDuplicados enLinea tipo="contacto" coincidencias={aviso.coincidencias} guardando={guardando}
              texto={aviso.tipo === 'nombre' ? t('duplicados.textoContactoNombre') : undefined}
              onAbrir={id => window.open(`/comercial/contactos?contacto=${id}`, '_blank', 'noopener')}
              onRevisar={() => setAviso(null)} onGuardarIgual={seguir} />
          )}
        </div>

        <Seccion titulo={t('ficha.identificacion')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Campo id="con-nombre" etiqueta={t('camposContacto.nombre')} obligatorio error={errores.nombre}>
              <input id="con-nombre" autoFocus={!contacto} value={form.nombre} onChange={e => cambiar('nombre', e.target.value)}
                aria-invalid={!!errores.nombre} className={`${entrada} ${errores.nombre ? 'border-red-300' : ''}`} />
            </Campo>
            <Campo id="con-apellidos" etiqueta={t('camposContacto.apellidos')}>
              <input id="con-apellidos" value={form.apellidos} onChange={e => cambiar('apellidos', e.target.value)} className={entrada} />
            </Campo>
          </div>
        </Seccion>

        {pide && (
          <Seccion titulo={t('fichaContacto.organizacion')}>
            {form.vinculos.map((v, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_12rem_auto] gap-2 items-start">
                <ComercialSelectorOrganizacion value={v.organizacion_id} aria-label={t('fichaContacto.organizacion')}
                  excluirIds={elegidas.filter(x => x !== v.organizacion_id)} crear={crearEnFila(i)}
                  onChange={x => cambiarLista('vinculos', l => cambiarFila(l, i, 'organizacion_id', x))} />
                <input value={v.cargo} onChange={e => cambiarLista('vinculos', l => cambiarFila(l, i, 'cargo', e.target.value))}
                  placeholder={t('vinculos.cargoPlaceholder')} aria-label={t('camposContacto.cargo')}
                  className={`${entrada} col-span-1 row-start-2 sm:row-start-auto`} />
                <BotonQuitar onClick={() => cambiarLista('vinculos', l => l.filter((_, j) => j !== i))} texto={t('camposContacto.quitar')} />
              </div>
            ))}
            <BotonAgregar onClick={() => cambiarLista('vinculos', l => [...l, { organizacion_id: null, cargo: '' }])}>
              {t('camposContacto.agregarOrganizacion')}
            </BotonAgregar>
          </Seccion>
        )}

        <Seccion titulo={t('fichaContacto.informacionContacto')}>
          <ListaMedios campo="correos" filas={form.correos} errores={errores} tipo="email"
            etiqueta={t('camposContacto.correos')} placeholder="nombre@empresa.cl"
            agregar={t('camposContacto.agregarCorreo')} cambiarLista={cambiarLista} />
          <ListaMedios campo="telefonos" filas={form.telefonos} errores={{}} tipo="tel"
            etiqueta={t('camposContacto.telefonos')} placeholder="+56 9 1234 5678"
            agregar={t('camposContacto.agregarTelefono')} cambiarLista={cambiarLista} />
          <Campo id="con-linkedin" etiqueta={t('camposContacto.linkedin')}>
            <input id="con-linkedin" value={form.linkedin} onChange={e => cambiar('linkedin', e.target.value)}
              placeholder="linkedin.com/in/…" inputMode="url" className={entrada} />
          </Campo>
        </Seccion>

        <Seccion titulo={t('camposContacto.etiquetas')}>
          {etiquetasOfrecidas.length
            ? <div role="group" aria-label={t('camposContacto.etiquetas')} className="flex flex-wrap gap-1.5">
                {etiquetasOfrecidas.map(x => {
                  const si = form.etiqueta_ids.includes(x.id)
                  return (
                    <button key={x.id} type="button" aria-pressed={si} onClick={() => alternarEtiqueta(x.id)}
                      className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${si
                        ? 'bg-violet-600 text-white ring-violet-600' : 'bg-white text-gray-600 ring-gray-200 hover:ring-gray-400'}`}>
                      {si ? '✓ ' : ''}{x.nombre}{!x.activo ? ` (${t('chips.inactivo').toLowerCase()})` : ''}
                    </button>
                  )
                })}
              </div>
            : <p className="text-xs text-gray-400">{t('camposContacto.sinEtiquetas')}</p>}
        </Seccion>

        <Seccion titulo={t('campos.descripcion')}>
          <textarea rows={4} value={form.observaciones} onChange={e => cambiar('observaciones', e.target.value)}
            className={`${entrada} resize-y`} aria-label={t('campos.descripcion')} />
        </Seccion>

        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 mt-6 px-4 sm:px-6 py-3 bg-white border-t border-gray-100 flex justify-end gap-2">
        <button type="button" onClick={cancelar}
          className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-700 hover:bg-gray-50">
          {t('catalogo.cancelar')}
        </button>
        <button type="submit" disabled={guardando}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40">
          {guardando ? t('form.guardando') : contacto ? t('form.guardarCambios') : t('formContacto.crear')}
        </button>
      </div>
    </form>
  )
}

// Emails or phones, each with the principal mark (one; the first one added is
// it, and removing it hands it to the first left). The description the server
// keeps per email and phone isn't asked any more: a saved one rides along
// untouched with its row (formularioDe keeps it, filasParaEnviar sends it back).
// The star shows only while there are two or more: a lone one is the principal
// anyway (agregarFila / quitarFila keep it so), so there is nothing to choose.
function ListaMedios({ campo, filas, errores, tipo, etiqueta, placeholder, agregar, cambiarLista }) {
  const { t } = useTranslation('comercial')
  const elegir = filas.length > 1
  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1">{etiqueta}</span>
      <div className="space-y-2">
        {filas.map((x, i) => {
          const err = errores[`correo-${i}`]
          return (
            <div key={i}>
              <div className={`grid ${elegir ? 'grid-cols-[auto_1fr_auto]' : 'grid-cols-[1fr_auto]'} gap-2 items-center`}>
                {elegir && (
                  <button type="button" onClick={() => cambiarLista(campo, l => marcarPrincipal(l, i))} aria-pressed={x.principal}
                    title={x.principal ? t('camposContacto.principal') : t('camposContacto.marcarPrincipal')}
                    aria-label={x.principal ? t('camposContacto.principal') : t('camposContacto.marcarPrincipal')}
                    className={`w-8 h-8 rounded-lg text-base leading-none ${x.principal ? 'text-amber-500' : 'text-gray-300 hover:text-gray-500'}`}>
                    {x.principal ? '★' : '☆'}
                  </button>
                )}
                <input type={tipo} value={x.valor} onChange={e => cambiarLista(campo, l => cambiarFila(l, i, 'valor', e.target.value))}
                  placeholder={placeholder} aria-label={etiqueta} aria-invalid={!!err}
                  className={`${entrada} ${err ? 'border-red-300' : ''}`} />
                <BotonQuitar onClick={() => cambiarLista(campo, l => quitarFila(l, i))} texto={t('camposContacto.quitar')} />
              </div>
              {err && <span className={`block text-xs text-red-600 mt-1 ${elegir ? 'ml-10' : ''}`}>{err}</span>}
            </div>
          )
        })}
      </div>
      <BotonAgregar onClick={() => cambiarLista(campo, agregarFila)}>{agregar}</BotonAgregar>
    </div>
  )
}

function BotonQuitar({ onClick, texto }) {
  return (
    <button type="button" onClick={onClick} title={texto} aria-label={texto}
      className="w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 text-lg leading-none">×</button>
  )
}

function BotonAgregar({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} className="mt-2 text-xs text-gray-600 hover:text-gray-900 underline">{children}</button>
  )
}

function Seccion({ titulo, children }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{titulo}</legend>
      {children}
    </fieldset>
  )
}

function Campo({ id, etiqueta, obligatorio, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {etiqueta}{obligatorio && <span className="text-red-500"> *</span>}
      </label>
      {children}
      {error && <span className="block text-xs text-red-600 mt-1">{error}</span>}
    </div>
  )
}
